/** Nested groups: turns the flat group list into a tree ordered by sortOrder then name. Orphans and cycles become roots. */
import type { Group } from "@hub/shared";

export interface GroupNode<G extends Group = Group> {
  group: G;
  depth: number;
  children: GroupNode<G>[];
}

function compare(a: Group, b: Group): number {
  return (a.sortOrder ?? "").localeCompare(b.sortOrder ?? "") || a.name.localeCompare(b.name);
}

export function buildGroupTree<G extends Group>(groups: G[]): GroupNode<G>[] {
  const byId = new Map(groups.map((g) => [g.id, g]));
  // A parent counts only when it exists in the list and does not lead back to the child (cycle guard).
  const parentOf = (g: G): string | null => {
    if (!g.parentId || g.parentId === g.id || !byId.has(g.parentId)) return null;
    let cur: G | undefined = byId.get(g.parentId);
    const seen = new Set<string>([g.id]);
    while (cur) {
      if (seen.has(cur.id)) return null;
      seen.add(cur.id);
      cur = cur.parentId ? byId.get(cur.parentId) : undefined;
    }
    return g.parentId;
  };
  const childrenOf = new Map<string | null, G[]>();
  for (const g of groups) {
    const p = parentOf(g);
    const list = childrenOf.get(p) ?? [];
    list.push(g);
    childrenOf.set(p, list);
  }
  const build = (parent: string | null, depth: number): GroupNode<G>[] =>
    (childrenOf.get(parent) ?? []).sort(compare).map((g) => ({ group: g, depth, children: build(g.id, depth + 1) }));
  return build(null, 0);
}

/** Depth-first flattening for list rendering; `collapsed` hides the subtrees of those ids. */
export function flattenTree<G extends Group>(nodes: GroupNode<G>[], collapsed: Set<string> = new Set()): GroupNode<G>[] {
  const out: GroupNode<G>[] = [];
  const walk = (list: GroupNode<G>[]) => {
    for (const n of list) {
      out.push(n);
      if (!collapsed.has(n.group.id)) walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

/** Ids a group may not choose as its parent: itself and everything below it. */
export function descendantIds(groups: Group[], groupId: string): Set<string> {
  const out = new Set<string>([groupId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const g of groups) {
      if (g.parentId && out.has(g.parentId) && !out.has(g.id)) {
        out.add(g.id);
        grew = true;
      }
    }
  }
  return out;
}
